import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import { Helmet } from 'react-helmet-async';

function BlogPost() {
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const { permalink } = useParams();
  const navigate = useNavigate();

  // SEO and Post Fetch
  useEffect(() => {
    const fetchPost = async () => {
      if (!permalink || permalink === 'undefined') {
        setError('Invalid post URL');
        document.title = 'Invalid URL | Choir Center Blog';
        return;
      }
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('permalink', permalink)
        .single();
      if (error) {
        setError(`Failed to load post: ${error.message}`);
        document.title = 'Error | Choir Center Blog';
        return;
      }
      if (!data) {
        setError('Post not found');
        document.title = 'Post Not Found | Choir Center Blog';
        return;
      }
      setPost(data);
    };

    const fetchUser = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session) {
        setUser(null);
        return;
      }
      const { data: userData } = await supabase.auth.getUser();
      setUser(userData?.user || null);
    };

    fetchPost();
    fetchUser();
    // eslint-disable-next-line
  }, [permalink]);

  useEffect(() => {
    if (post) fetchComments();
    // eslint-disable-next-line
  }, [post]);

  // Fetch comments for post
  const fetchComments = async () => {
    const { data: commentData, error: commentError } = await supabase
      .from('comments')
      .select('id, content, created_at, user_id')
      .eq('post_id', post.id)
      .order('created_at', { ascending: true });
    if (commentError) {
      setError('Failed to load comments: ' + commentError.message);
      return;
    }

    const userIds = commentData.map(comment => comment.user_id);
    const { data: profileData } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds);

    const enrichedComments = commentData.map(comment => ({
      ...comment,
      profiles: profileData?.find(profile => profile.id === comment.user_id) || { full_name: 'Unknown' }
    }));
    setComments(enrichedComments || []);
  };

  // Handle comment posting
  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to comment.');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([{ post_id: post.id, user_id: user.id, content: newComment }])
        .select('id, content, created_at, user_id')
        .single();
      if (error) throw error;
      setComments(prev =>
        [
          ...prev,
          {
            ...data,
            profiles: { full_name: user.user_metadata?.full_name || 'You' }
          }
        ]
      );
      setNewComment('');
    } catch (err) {
      setError('Failed to add comment: ' + err.message);
    }
  };

  // Handle comment delete
  const handleCommentDelete = async (commentId) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      try {
        const { error } = await supabase
          .from('comments')
          .delete()
          .eq('id', commentId);
        if (error) throw error;
        setComments(comments.filter(comment => comment.id !== commentId));
      } catch (err) {
        setError('Failed to delete comment: ' + err.message);
      }
    }
  };

  // Social sharing
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || 'Check out this blog post!';

  // Prepare SEO variables
  const postTitle = post ? `${post.title} | Choir Center Blog` : 'Choir Center Blog';
  const postDescription = post?.content
    ? post.content.replace(/<[^>]+>/g, '').substring(0, 155)
    : 'Read inspiring blog posts on Choir Center';
  const postUrl = `https://choircenter.com/blog/${post?.permalink?.toLowerCase() || ''}`;
  const postImage = "https://choircenter.com/blog-cover.jpg"; // Change if using per-post images
  const postAuthor = post?.author_name || "Choir Center";
  const postDate = post?.created_at || "";

  // BlogPosting JSON-LD Schema
  const blogPostingJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": postDescription,
    "image": postImage,
    "author": {
      "@type": "Person",
      "name": postAuthor
    },
    "publisher": {
      "@type": "Organization",
      "name": "Choir Center",
      "logo": {
        "@type": "ImageObject",
        "url": "https://choircenter.com/images/logo.png"
      }
    },
    "datePublished": postDate,
    "dateModified": post?.updated_at || postDate,
    "mainEntityOfPage": postUrl,
    "url": postUrl,
    "keywords": (typeof post.tags === 'string'
      ? post.tags
      : Array.isArray(post.tags)
        ? post.tags.join(', ')
        : ''
    ) + ', choir, choir center, music, blog'
  };

  // BreadcrumbList JSON-LD
  const breadcrumbJsonLd = post && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://choircenter.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://choircenter.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": postUrl
      }
    ]
  };

  if (!post && !error)
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] bg-gradient-to-br from-green-50 to-white">
        <section className="w-full mb-4"><AdBanner position="other_pages_below_header" /></section>
        <div className="text-lg text-green-700 animate-pulse font-bold">Loading...</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white py-0 px-1">
      <Helmet>
        <title>{postTitle}</title>
        <meta name="description" content={postDescription} />
        <link rel="canonical" href={postUrl} />
        {/* Open Graph */}
        <meta property="og:title" content={post?.title || ''} />
        <meta property="og:description" content={postDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={postUrl} />
        <meta property="og:image" content={postImage} />
        {/* Twitter */}
        <meta name="twitter:title" content={post?.title || ''} />
        <meta name="twitter:description" content={postDescription} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={postImage} />
        <meta name="author" content={postAuthor} />
        {post && <meta name="keywords" content={
          (typeof post.tags === 'string'
            ? post.tags
            : Array.isArray(post.tags)
              ? post.tags.join(', ')
              : ''
          ) + ', choir, choir center, music, blog'
        } />}
        {/* JSON-LD Structured Data */}
        {post && (
          <>
            <script type="application/ld+json">
              {JSON.stringify(blogPostingJsonLd)}
            </script>
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbJsonLd)}
            </script>
          </>
        )}
      </Helmet>

      <section className="w-full max-w-3xl mx-auto mt-4 mb-6">
        <AdBanner position="other_pages_below_header" />
      </section>

      <nav aria-label="Breadcrumb" className="breadcrumb-nav mb-5 mt-1">
        <ol className="flex flex-wrap gap-1 text-green-700 text-sm" style={{fontWeight:'bold'}}>
          <li>
            <Link to="/" className="hover:underline">Home</Link>
            <span aria-hidden="true" className="mx-2 text-gray-400">/</span>
          </li>
          <li>
            <Link to="/blog" className="hover:underline">Blog</Link>
            <span aria-hidden="true" className="mx-2 text-gray-400">/</span>
          </li>
          <li className="text-green-900">
            {post?.title}
          </li>
        </ol>
      </nav>

      <main className="w-full max-w-3xl mx-auto bg-white rounded-3xl shadow-lg px-4 sm:px-10 py-8">
        {/* ERROR */}
        {error ? (
          <div className="bg-red-50 border border-red-300 text-red-800 rounded-xl px-6 py-8 text-center shadow">
            <h1 className="text-2xl font-bold mb-2">Error</h1>
            <p className="mb-6" aria-live="polite">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-2 inline-block bg-green-600 text-white rounded-full px-5 py-2 font-semibold hover:bg-green-700 transition"
            >
              Back to Home
            </button>
          </div>
        ) : (
          <>
            {/* Blog Cover / Title */}
            <article>
              <header className="mb-7 flex flex-col items-center text-center">
                <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-br from-green-900 via-green-600 to-lime-500 bg-clip-text text-transparent drop-shadow mb-3">
                  {post?.title}
                </h1>
                <div className="text-green-700/90 text-sm md:text-base mb-2">
                  <span>
                    {post?.created_at && (
                      <>
                        Posted on <time dateTime={post.created_at}>{new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                      </>
                    )}
                  </span>
                </div>
                {/* Robust tags rendering */}
                {post?.tags && Array.isArray(post.tags)
                  ? (
                      <div className="flex flex-wrap gap-2 justify-center mt-1">
                        {post.tags.map((tag, idx) => (
                          <span key={tag || idx} className="bg-lime-200 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">{tag?.toString().trim()}</span>
                        ))}
                      </div>
                    )
                  : typeof post?.tags === 'string' && post.tags.trim() !== ''
                    ? (
                      <div className="flex flex-wrap gap-2 justify-center mt-1">
                        {post.tags.split(',').map(tag => (
                          <span key={tag.trim()} className="bg-lime-200 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">{tag.trim()}</span>
                        ))}
                      </div>
                    )
                  : null
                }
              </header>
              {/* Blog Content */}
              <section
                className="prose prose-green prose-lg md:prose-xl max-w-none prose-h2:text-green-700 prose-h3:text-green-700 prose-a:text-green-700 prose-a:underline hover:prose-a:text-green-900 prose-blockquote:border-lime-400 prose-img:rounded-xl prose-img:mx-auto prose-img:my-6 prose-p:leading-relaxed prose-p:text-[1.18em] prose-p:text-green-900 prose-strong:text-green-800 prose-li:marker:text-green-400 prose-ul:list-disc prose-ol:list-decimal prose-table:border prose-table:border-green-100 prose-th:bg-green-100 prose-th:text-green-900 prose-code:bg-green-50 prose-code:text-green-700 prose-code:rounded-md prose-code:px-1 prose-code:py-0.5"
                dangerouslySetInnerHTML={{ __html: post?.content }}
                itemProp="articleBody"
              />
            </article>

            {/* Social Share */}
            <div className="flex items-center gap-4 mt-8 mb-4 justify-center">
              <span className="font-semibold text-green-700">Share:</span>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 shadow transition"
                aria-label="Share on Facebook"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
              </a>
              <a
                href={`https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-black hover:bg-gray-800 shadow transition"
                aria-label="Share on X (Twitter)"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-green-500 hover:bg-green-600 shadow transition"
                aria-label="Share on WhatsApp"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.36 5.07L2 22l4.93-1.36C8.42 21.5 10.15 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm3.97 15.03c-.25.7-.76 1.27-1.45 1.6-.7.34-1.6.5-2.52.25-1.8-.5-3.3-2-3.8-3.8-.25-.9-.08-1.8.25-2.52.33-.7.9-1.2 1.6-1.45.15-.05.3-.08.45-.08s.3.03.45.08c.35.15.6.45.7.8.1.35.15.7.15 1.05 0 .35-.05.7-.15 1.05-.5 1.5.5 2.5 1.5 3s2-.5 1.5-1.5c-.15-.35-.2-.7-.15-1.05s.2-.7.55-.85c.15-.05.3-.08.45-.08s.3.03.45.08z"/></svg>
              </a>
            </div>

            {/* Comments Section */}
            <div className="border-t pt-8 mt-10">
              <button
                className="mb-3 text-green-800 font-bold hover:text-green-600 text-lg transition"
                onClick={() => setIsCommentsOpen(!isCommentsOpen)}
              >
                {isCommentsOpen ? 'Hide Comments' : `Show Comments (${comments.length})`}
              </button>
              {isCommentsOpen && (
                <div>
                  <div className="space-y-6 mb-7">
                    {comments.length > 0 ? (
                      comments.map(comment => (
                        <article key={comment.id} className="rounded-xl bg-lime-50 border border-green-100 px-4 py-3 flex flex-col md:flex-row gap-2">
                          <div className="flex-1">
                            <div className="text-green-900 text-base font-medium">{comment.profiles?.full_name || 'Unknown'}</div>
                            <div className="text-gray-700 text-sm mt-1 whitespace-pre-line">{comment.content}</div>
                          </div>
                          <div className="flex flex-col md:items-end md:justify-between min-w-[120px]">
                            <span className="text-xs text-green-600 mb-1">{new Date(comment.created_at).toLocaleString()}</span>
                            {user && user.id === comment.user_id && (
                              <button
                                onClick={() => handleCommentDelete(comment.id)}
                                className="text-red-600 hover:underline text-xs font-bold"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </article>
                      ))
                    ) : (
                      <p className="text-gray-400 italic">No comments yet.</p>
                    )}
                  </div>
                  {user ? (
                    <form onSubmit={handleCommentSubmit} className="mt-4 flex flex-col gap-3 bg-lime-50 rounded-xl border border-green-100 p-4">
                      <label htmlFor="comment" className="font-semibold text-green-800">Add your comment:</label>
                      <textarea
                        id="comment"
                        value={newComment}
                        onChange={e => setNewComment(e.target.value)}
                        placeholder="Share your thoughts..."
                        className="resize-none rounded-md border border-green-200 focus:ring-2 focus:ring-green-300 p-2 min-h-[70px] text-base text-green-900"
                        required
                        maxLength={800}
                        aria-live="polite"
                      />
                      <button
                        type="submit"
                        className="self-end bg-green-600 text-white px-5 py-2 rounded-full font-bold hover:bg-green-700 shadow transition"
                      >
                        Post Comment
                      </button>
                      {error && (
                        <div className="text-red-700 text-sm mt-2" aria-live="polite">
                          {error}
                        </div>
                      )}
                    </form>
                  ) : (
                    <p className="text-green-600 text-sm mt-3">Please <a href="/login" className="underline hover:text-green-800">log in</a> to comment.</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default BlogPost;
